import React from 'react'
import {
  DataGrid,
  useGridApiRef
} from '@mui/x-data-grid'
import notFound from "../../containers/Contacts/Table/NotFoundData"
import { formatMoney } from "../../utils/amount"
import VerifiedAccountNumber from "./VerifiedAccountNumber"
import AccountStatusCell from "./AccountStatusCell"
import DebtWorkStatusCell from "./DebtWorkStatusCell"
import ProviderBadge from './ProviderBadge'
import { describeDebtOwner, hasDebtOwnerAttribution, DEBT_OWNER_HEADER } from './debtOwner'

// Local styled component removed in favor of global theme overrides

const CreditReportsTable = ({ data = [], workStatusOn = false, canEditWorkStatus = false, onOpenWorkStatus }) => {
  const apiRef = useGridApiRef()
  const progressColumn = {
    field: 'workStatus',
    headerName: 'Progress',
    renderCell: (params) => (
      <DebtWorkStatusCell debt={params.row} canEdit={canEditWorkStatus} onOpen={onOpenWorkStatus} tabIndex={params.tabIndex} />
    ),
    sortable: false,
    flex: 1,
    minWidth: 140
  }
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      flex: 1
    },
    {
      "field": "bankAccount",
      "headerName": "Creditor",
      renderCell: (params) => {
        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span>{params?.row?.bankAccount || params?.row?.creditorName}</span>
              <ProviderBadge sourceType={params?.row?.sourceType} />
            </div>
            {params?.row?.originalCreditorName && (
              <div className="text-text-secondary text-[12px]">
                Original: {params.row.originalCreditorName}
              </div>
            )}
          </div>
        )
      },
      sortable: false,
      flex: 1,
      // widest column: keeps flex to absorb slack, minWidth stops long creditor names clipping when crowded
      minWidth: 220
    },
    {
      "field": "verifiedAccountNumber",
      "headerName": "Verified Account #",
      renderCell: (params) => {
        return (
          <VerifiedAccountNumber creditProfileId={params?.row?._id}
            verifiedNumber={params?.row?.verifiedAccountNumber} />
        )
      },
      sortable: false,
      flex: 1,
      // minWidth clears the long ALL-CAPS "VERIFIED ACCOUNT #" header + account number so it can't clamp to 50px
      minWidth: 200
    },
    {
      "field": "accountNumber",
      "headerName": "Account #",
      sortable: false,
      flex: 1,
      // minWidth fits the account number and stops flex clamping to the 50px floor
      minWidth: 140
    },
    {
      "field": "accountType",
      "headerName": "Account Type",
      renderCell: (params) => {
        return (
          <div className="w-full text-wrap line-clamp-3">{params?.row?.accountType?.description}</div>
        )
      },
      sortable: false,
      flex: 1,
      // minWidth clears the "ACCOUNT TYPE" header and gives the wrapped description room
      minWidth: 160
    },
    {
      "field": "accountStatus",
      "headerName": "Account Status",
      renderCell: (params) => {
        return (
          <AccountStatusCell
            creditProfileId={params?.row?._id}
            currentStatus={params?.row?.accountStatus}
          />
        )
      },
      sortable: false,
      flex: 1,
      // minWidth clears the "ACCOUNT STATUS" header and the status control so neither clips
      minWidth: 160
    },
    ...(workStatusOn ? [progressColumn] : []),
    {
      "field": "debtOwner",
      "headerName": DEBT_OWNER_HEADER,
      renderCell: (params) => {
        return (
          <div className="">{describeDebtOwner(params?.row?.source)}</div>
        )
      },
      sortable: false,
      flex: 1,
      minWidth: 130
    },
    {
      "field": "originalLoanAmount",
      "headerName": "Current Amount",
      renderCell: (params) => {
        return (
          <div className="">{formatMoney(params?.row?.balanceAmount)}</div>
        )
      },
      sortable: false,
      flex: 1,
      // minWidth clears the "CURRENT AMOUNT" header and formatted money value
      minWidth: 150
    },
  ]

  return (
    <div>
      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={75}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
        }}
        columnVisibilityModel={{
          id: false,
          debtOwner: hasDebtOwnerAttribution(data)
        }}
        rows={data?.filter(d => d?.isDebt)}
        apiRef={apiRef}
        hideFooterPagination={true}
        disableRowSelectionOnClick
        disableColumnSelector
        getRowClassName={(params) => params?.row?.status === 'Failed' ? 'bg-error-200' : ''}
      />

    </div>
  )
}

export default CreditReportsTable
